import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MonthlyExpansionDialog } from "./MonthlyExpansionDialog";
import { Trash2, Download, FileText, Save, ChevronDown, ChevronUp, Eye, Pencil, Calendar } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import {
  useOrganizationQuestionnaires,
  useCreateQuestionnaire,
  useUpdateQuestionnaire,
  useDeleteQuestionnaire,
  type OrganizationQuestionnaire as QuestionnaireType,
} from "@/hooks/use-organization-questionnaires";
import {
  DEFAULT_FORM_DATA,
  DEFAULT_MONTHLY_EXPANSION,
  MONTHS,
  DEPLOYMENT_DURATION_OPTIONS,
  PRIMARY_OBJECTIVE_OPTIONS,
  SITE_CONDITION_OPTIONS,
  MOUNTING_TYPE_OPTIONS,
  CAPTURE_FREQUENCY_OPTIONS,
  OPERATING_HOURS_OPTIONS,
  HISTORICAL_STORAGE_OPTIONS,
  AI_DETECTION_OPTIONS,
  ALERT_BEHAVIOUR_OPTIONS,
  AI_QUERIES_PER_IMAGE_OPTIONS,
  ALERT_CHANNEL_OPTIONS,
  PLATFORM_FEATURE_OPTIONS,
  FREQUENCY_TO_IMAGES_PER_DAY,
  AI_QUERY_MAP,
  type QuestionnaireFormData,
  type CalculatedResults,
  type MonthlyExpansion,
} from "@/constants/questionnaire";

interface OrganizationQuestionnaireProps {
  organizationId: string;
  organizationName: string;
}

export const OrganizationQuestionnaire = ({
  organizationId,
  organizationName,
}: OrganizationQuestionnaireProps) => {
  const { data: questionnaires = [], isLoading } = useOrganizationQuestionnaires(organizationId);
  const createQuestionnaire = useCreateQuestionnaire();
  const updateQuestionnaire = useUpdateQuestionnaire();
  const deleteQuestionnaire = useDeleteQuestionnaire();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [form, setForm] = useState<QuestionnaireFormData>({ ...DEFAULT_FORM_DATA });
  const [expansionDialogYear, setExpansionDialogYear] = useState<"2026" | "2027" | "2028" | null>(null);

  const imagesPerDay =
    form.captureFrequency === "Custom schedule"
      ? Number(form.customImagesPerDay || 0)
      : FREQUENCY_TO_IMAGES_PER_DAY[form.captureFrequency] || 0;

  const aiQueriesPerImage =
    form.aiQueriesPerImage === "Custom"
      ? Number(form.aiQueriesPerImageCustom || 0)
      : AI_QUERY_MAP[form.aiQueriesPerImage] || 0;

  const totals: CalculatedResults = useMemo(() => {
    const totalCameras = Number(form.camerasInitial || 0) + Number(form.cameras2026 || 0) + Number(form.cameras2027 || 0) + Number(form.cameras2028 || 0);
    const monthlyImagesPerCamera = imagesPerDay * 30;
    const monthlyImages = totalCameras * monthlyImagesPerCamera;
    const monthlyAiQueries = form.aiRequired === "Yes" ? monthlyImages * aiQueriesPerImage : 0;

    return { monthlyImages, monthlyAiQueries, packageRecommendation: "", imagesPerDay, aiQueriesPerImage, monthlyImagesPerCamera, totalCameras };
  }, [form, imagesPerDay, aiQueriesPerImage]);

  const updateField = (field: keyof QuestionnaireFormData, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };


  const toggleArrayValue = (field: keyof QuestionnaireFormData, value: string) => {
    setForm((prev) => {
      const current = (prev[field] as string[]) || [];
      const exists = current.includes(value);
      return { ...prev, [field]: exists ? current.filter((item) => item !== value) : [...current, value] };
    });
  };

  const handleSave = () => {
    if (editingId) {
      updateQuestionnaire.mutate(
        { id: editingId, organizationId, formData: form, calculatedResults: totals },
        {
          onSuccess: () => {
            setShowForm(false);
            setEditingId(null);
            setForm({ ...DEFAULT_FORM_DATA });
          },
        }
      );
    } else {
      createQuestionnaire.mutate(
        { organizationId, formData: form, calculatedResults: totals },
        {
          onSuccess: () => {
            setShowForm(false);
            setForm({ ...DEFAULT_FORM_DATA });
          },
        }
      );
    }
  };

  const handleEdit = (questionnaire: QuestionnaireType) => {
    const fd = questionnaire.form_data;
    setForm({
      ...DEFAULT_FORM_DATA,
      ...fd,
      monthlyExpansion: fd.monthlyExpansion || { ...DEFAULT_MONTHLY_EXPANSION },
    });
    setEditingId(questionnaire.id);
    setViewingId(null);
    setShowForm(true);
  };

  const handleExportJson = (questionnaire?: QuestionnaireType) => {
    const data = questionnaire
      ? { ...questionnaire.form_data, ...questionnaire.calculated_results, generatedAt: questionnaire.created_at }
      : { ...form, ...totals, generatedAt: new Date().toISOString() };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `questionnaire-${organizationName.toLowerCase().replace(/\s+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("JSON exported");
  };

  const handleExportPdf = (questionnaire?: QuestionnaireType) => {
    const formData = questionnaire ? questionnaire.form_data : form;
    const results = questionnaire ? questionnaire.calculated_results : totals;
    const dateStr = questionnaire
      ? new Date(questionnaire.created_at).toLocaleDateString("en-GB")
      : new Date().toLocaleDateString("en-GB");

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/snapshot-logo.png";

    const buildPdf = (logoLoaded: boolean) => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 15;

      // -- Logo --
      if (logoLoaded) {
        try {
          doc.addImage(img, "PNG", 14, y, 40, 14);
        } catch {
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text("SNAPSHOT LIVE", 14, y + 10);
        }
      } else {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("SNAPSHOT LIVE", 14, y + 10);
      }

      // Date top-right
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 120, 120);
      doc.text(dateStr, pageWidth - 14, y + 6, { align: "right" });
      doc.setTextColor(0, 0, 0);

      y += 22;

      // -- Header bar --
      doc.setFillColor(30, 30, 30);
      doc.rect(0, y, pageWidth, 16, "F");
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("Customer Requirements", 14, y + 11);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(organizationName, pageWidth - 14, y + 11, { align: "right" });
      doc.setTextColor(0, 0, 0);

      y += 24;

      // -- Summary metrics --
      const totalCameras = results.totalCameras ?? (formData.camerasInitial + (formData.cameras2026 || 0) + (formData.cameras2027 || 0) + (formData.cameras2028 || 0));
      const monthlyPerCam = results.monthlyImagesPerCamera ?? (results.imagesPerDay * 30);
      (doc as any).autoTable({
        startY: y,
        head: [["Images/Day/Cam", "Monthly/Cam", "Total Cameras", "Monthly Total"]],
        body: [[
          String(results.imagesPerDay),
          monthlyPerCam.toLocaleString(),
          String(totalCameras),
          results.monthlyImages.toLocaleString(),
        ]],
        styles: { fontSize: 9, halign: "center", cellPadding: 4 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: "bold", fontSize: 7 },
        bodyStyles: { fontStyle: "bold", fontSize: 11 },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 10;

      // -- Section tables --
      const sections: [string, [string, string][]][] = [
        ["Application Overview", [
          ["Application", formData.projectName || "-"],
          ["Number of Locations", String(formData.locations)],
          ["Deployment Duration", formData.deploymentDuration],
          ["Primary Objective", formData.primaryObjective],
        ]],
        ["Camera Deployment", [
          ["Initial Cameras", String(formData.camerasInitial)],
          ["Additional 2026", String(formData.cameras2026 ?? 0)],
          ["Additional 2027", String(formData.cameras2027 ?? 0)],
          ["Additional 2028", String(formData.cameras2028 ?? 0)],
          ["Total Cameras", String(totalCameras)],
          ["Site Conditions", formData.siteConditions?.join(", ") || "None"],
          ["Mounting Type", formData.mountingType],
        ]],
        ["Image Capture & Storage", [
          ["Capture Frequency", formData.captureFrequency],
          ["Operating Hours", formData.operatingHours],
          ["On-demand Images", formData.onDemandImages || "No"],
          ["Historical Storage", formData.historicalStorage],
        ]],
        ["AI & Analytics", [
          ["AI Required", formData.aiRequired],
          ["AI Detections", formData.aiDetections?.join(", ") || "None"],
          ["Alert Behaviour", formData.alertBehaviour],
          ["AI Queries per Image", formData.aiQueriesPerImage],
        ]],
        ["Alerts & Notifications", [
          ["Alert Channels", formData.alertChannels?.join(", ") || "None"],
          ["Alert Recipients", formData.alertRecipients?.join(", ") || "None"],
        ]],
        ["Platform & Integrations", [
          ["Platform Access", formData.platformAccess || "Yes"],
          ["Users Requiring Access", String(formData.usersRequiringAccess)],
          ["Platform Features", formData.platformFeatures?.join(", ") || "None"],
          ["Integrations", formData.integrations?.join(", ") || "None"],
          ...(formData.customIntegration ? [["Custom Integration", formData.customIntegration] as [string, string]] : []),
        ]],
        ["Compliance & Data", [
          ["Compliance Requirements", formData.compliance?.join(", ") || "None"],
          ["Data Retention", formData.dataRetention],
        ]],
        ["Commercial", [
          ["Commercial Model", formData.commercialModel],
          ["Budget Range", formData.budgetRange || "Not specified"],
          ["Success Criteria", formData.successCriteria || "Not specified"],
        ]],
      ];

      for (const [title, fields] of sections) {
        if (y > 250) { doc.addPage(); y = 15; }

        (doc as any).autoTable({
          startY: y,
          head: [[{ content: title, colSpan: 2, styles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: "bold", fontSize: 10 } }]],
          body: fields.map(([label, value]) => [label, value]),
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 55, textColor: [80, 80, 80] },
            1: { cellWidth: "auto" },
          },
          margin: { left: 14, right: 14 },
          theme: "plain",
          tableLineWidth: 0.1,
          tableLineColor: [220, 220, 220],
        });

        y = (doc as any).lastAutoTable.finalY + 6;
      }

      // -- Notes --
      if (formData.notes) {
        if (y > 240) { doc.addPage(); y = 15; }

        (doc as any).autoTable({
          startY: y,
          head: [[{ content: "Notes", colSpan: 1, styles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: "bold", fontSize: 10 } }]],
          body: [[formData.notes]],
          styles: { fontSize: 9, cellPadding: 4 },
          margin: { left: 14, right: 14 },
          theme: "plain",
          tableLineWidth: 0.1,
          tableLineColor: [220, 220, 220],
        });

        y = (doc as any).lastAutoTable.finalY + 6;
      }

      // -- Footer on every page --
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setDrawColor(200, 200, 200);
        doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);
        doc.setFontSize(7);
        doc.setTextColor(140, 140, 140);
        doc.text(
          "Snapshot Live Limited · DOCK 2/006 · 85 Explorations Drive · LE4 5NU, Leicester, United Kingdom · Registration: 16174031",
          pageWidth / 2,
          pageHeight - 12,
          { align: "center" }
        );
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - 14,
          pageHeight - 12,
          { align: "right" }
        );
      }

      doc.save(`questionnaire-${organizationName.toLowerCase().replace(/\s+/g, "-")}.pdf`);
      toast.success("PDF exported");
    };

    img.onload = () => buildPdf(true);
    img.onerror = () => buildPdf(false);
  };

  const viewingQuestionnaire = questionnaires.find((q) => q.id === viewingId);

  // Viewing a saved questionnaire
  if (viewingQuestionnaire) {
    const fd = viewingQuestionnaire.form_data;
    const cr = viewingQuestionnaire.calculated_results;
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Questionnaire Results</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleEdit(viewingQuestionnaire)}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExportJson(viewingQuestionnaire)}>
              <Download className="h-4 w-4 mr-2" /> JSON
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExportPdf(viewingQuestionnaire)}>
              <FileText className="h-4 w-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewingId(null)}>Back</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Images/day/camera</p>
            <p className="text-2xl font-semibold">{cr.imagesPerDay}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Monthly images/camera</p>
            <p className="text-2xl font-semibold">{(cr.monthlyImagesPerCamera ?? cr.imagesPerDay * 30).toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Total cameras</p>
            <p className="text-2xl font-semibold">{(cr.totalCameras ?? fd.camerasInitial).toLocaleString()}</p>
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Project", [["Project", fd.projectName], ["Locations", fd.locations], ["Duration", fd.deploymentDuration], ["Objective", fd.primaryObjective]]],
            ["Cameras", [["Initial cameras", fd.camerasInitial], ["Additional 2026", fd.cameras2026 ?? 0], ["Additional 2027", fd.cameras2027 ?? 0], ["Additional 2028", fd.cameras2028 ?? 0], ["Mounting", fd.mountingType], ["Conditions", fd.siteConditions?.join(", ") || "None"]]],
            ["Capture", [["Frequency", fd.captureFrequency], ["Hours", fd.operatingHours], ["On-demand", fd.onDemandImages], ["Storage", fd.historicalStorage]]],
            ["AI", [["Required", fd.aiRequired], ["Detections", fd.aiDetections?.join(", ") || "None"], ["Behaviour", fd.alertBehaviour]]],
            ["Platform", [["Access", fd.platformAccess], ["Users", fd.usersRequiringAccess], ["Features", fd.platformFeatures?.join(", ") || "None"]]],
            ["Commercial", [["Model", fd.commercialModel], ["Budget", fd.budgetRange || "-"], ["Criteria", fd.successCriteria || "-"]]],
          ].map(([title, fields]) => (
            <Card key={title as string}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{title as string}</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {(fields as [string, any][]).map(([l, v]) => (
                  <div key={l} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{l}</span>
                    <span className="font-medium text-right max-w-[60%] truncate">{String(v)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {fd.notes && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{fd.notes}</p></CardContent>
          </Card>
        )}
      </div>
    );
  }

  // List view
  if (!showForm) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Sales Questionnaires</h3>
          <Button onClick={() => setShowForm(true)} className="bg-[hsl(60,73%,76%)] text-black border border-gray-300 hover:bg-[hsl(60,73%,70%)]">
            New Questionnaire
          </Button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : questionnaires.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No questionnaires yet. Create one to scope a package.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {questionnaires.map((q) => (
              <Card key={q.id}>
                <CardContent className="flex items-center justify-between py-4">
                  <div>
                    <p className="font-medium">{q.form_data.projectName || "Untitled"}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(q.created_at).toLocaleDateString()} · {q.form_data.camerasInitial} cameras
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(q)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setViewingId(q.id)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportJson(q)}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportPdf(q)}>
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteQuestionnaire.mutate({ id: q.id, organizationId })}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form sections helper components
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );

  const FormInput = ({ label, field, type = "text", placeholder = "" }: { label: string; field: keyof QuestionnaireFormData; type?: string; placeholder?: string }) => (
    <div className="space-y-1">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <input
        key={field}
        type="text"
        inputMode={type === "number" ? "numeric" : "text"}
        pattern={type === "number" ? "[0-9]*" : undefined}
        defaultValue={String(form[field] || "")}
        onBlur={(e) => updateField(field, type === "number" ? Number(e.target.value) : e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { updateField(field, type === "number" ? Number(e.currentTarget.value) : e.currentTarget.value); } }}
        placeholder={placeholder}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </div>
  );

  const FormSelect = ({ label, field, options }: { label: string; field: keyof QuestionnaireFormData; options: readonly string[] }) => (
    <div className="space-y-1">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <Select value={String(form[field])} onValueChange={(v) => updateField(field, v)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const CheckboxGroup = ({ label, field, options }: { label: string; field: keyof QuestionnaireFormData; options: readonly string[] }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={(form[field] as string[])?.includes(option)}
              onCheckedChange={() => toggleArrayValue(field, option)}
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  );

  // Form view
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{editingId ? "Edit Questionnaire" : "New Questionnaire"}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingId(null); setForm({ ...DEFAULT_FORM_DATA }); }}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={createQuestionnaire.isPending || updateQuestionnaire.isPending} className="bg-[hsl(60,73%,76%)] text-black border border-gray-300 hover:bg-[hsl(60,73%,70%)]">
            <Save className="h-4 w-4 mr-2" />
            {(createQuestionnaire.isPending || updateQuestionnaire.isPending) ? "Saving..." : editingId ? "Update" : "Save"}
          </Button>
        </div>
      </div>

      {/* Live estimate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground uppercase">Images/day/camera</p>
          <p className="text-2xl font-semibold">{imagesPerDay.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground uppercase">Monthly images/camera</p>
          <p className="text-2xl font-semibold">{totals.monthlyImagesPerCamera.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground uppercase">Total cameras</p>
          <p className="text-2xl font-semibold">{totals.totalCameras.toLocaleString()}</p>
        </CardContent></Card>
      </div>

      <div className="space-y-6">
        <Section title="1. Application Overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Application" field="projectName" placeholder="e.g. Highway M1 Phase 3" />
            <FormInput label="Number of locations" field="locations" type="number" />
            <FormSelect label="Deployment duration" field="deploymentDuration" options={DEPLOYMENT_DURATION_OPTIONS} />
            <FormSelect label="Primary objective" field="primaryObjective" options={PRIMARY_OBJECTIVE_OPTIONS} />
          </div>
        </Section>

        <Section title="2. Camera Deployment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Initial cameras" field="camerasInitial" type="number" />
            <FormSelect label="Mounting type" field="mountingType" options={MOUNTING_TYPE_OPTIONS} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Planned Expansion</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(["2026", "2027", "2028"] as const).map((year) => {
                const fieldKey = `cameras${year}` as "cameras2026" | "cameras2027" | "cameras2028";
                return (
                  <div key={year} className="space-y-1">
                    <label className="text-xs text-muted-foreground">Additional cameras {year}</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={form[fieldKey]}
                        readOnly
                        className="bg-muted cursor-default"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setExpansionDialogYear(year)}
                        title={`Monthly breakdown for ${year}`}
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monthly expansion dialog */}
          {expansionDialogYear && (
            <MonthlyExpansionDialog
              year={expansionDialogYear}
              initialData={form.monthlyExpansion?.[expansionDialogYear] || {}}
              onClose={() => setExpansionDialogYear(null)}
              onSave={(monthlyData) => {
                const yearTotal = Object.values(monthlyData).reduce((s: number, v: number) => s + (v || 0), 0);
                const yearField = `cameras${expansionDialogYear}` as "cameras2026" | "cameras2027" | "cameras2028";
                setForm((prev) => ({
                  ...prev,
                  monthlyExpansion: {
                    ...(prev.monthlyExpansion || DEFAULT_MONTHLY_EXPANSION),
                    [expansionDialogYear]: monthlyData,
                  },
                  [yearField]: yearTotal,
                }));
                setExpansionDialogYear(null);
              }}
            />
          )}
          <CheckboxGroup label="Site conditions" field="siteConditions" options={SITE_CONDITION_OPTIONS} />
        </Section>

        <Section title="3. Image Capture Requirements">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Capture frequency" field="captureFrequency" options={CAPTURE_FREQUENCY_OPTIONS} />
            <FormSelect label="Operating hours" field="operatingHours" options={OPERATING_HOURS_OPTIONS} />
            {form.captureFrequency === "Custom schedule" && (
              <FormInput label="Custom images per day" field="customImagesPerDay" type="number" />
            )}
            <FormSelect label="On-demand images" field="onDemandImages" options={["Yes", "No"]} />
            <FormSelect label="Historical storage" field="historicalStorage" options={HISTORICAL_STORAGE_OPTIONS} />
          </div>
        </Section>

        <Section title="4. AI & Analytics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="AI required" field="aiRequired" options={["Yes", "No"]} />
            {form.aiRequired === "Yes" && (
              <>
                <FormSelect label="AI queries per image" field="aiQueriesPerImage" options={AI_QUERIES_PER_IMAGE_OPTIONS} />
                {form.aiQueriesPerImage === "Custom" && (
                  <FormInput label="Custom AI queries per image" field="aiQueriesPerImageCustom" type="number" />
                )}
                <FormSelect label="Alert behaviour" field="alertBehaviour" options={ALERT_BEHAVIOUR_OPTIONS} />
              </>
            )}
          </div>
          {form.aiRequired === "Yes" && (
            <CheckboxGroup label="AI detections" field="aiDetections" options={AI_DETECTION_OPTIONS} />
          )}
        </Section>

        <Section title="5. Alerts & Notifications">
          <CheckboxGroup label="Alert channels" field="alertChannels" options={ALERT_CHANNEL_OPTIONS} />
        </Section>

        <Section title="6. Platform & Reporting">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Platform access required" field="platformAccess" options={["Yes", "No"]} />
            {form.platformAccess === "Yes" && (
              <FormInput label="Users requiring access" field="usersRequiringAccess" type="number" />
            )}
          </div>
          {form.platformAccess === "Yes" && (
            <CheckboxGroup label="Platform features" field="platformFeatures" options={PLATFORM_FEATURE_OPTIONS} />
          )}
        </Section>

        <Section title="7. Integrations, Compliance & Commercial">
          <Textarea
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Enter any details about integrations, compliance requirements, data retention, commercial model, budget, success criteria, or other notes..."
            rows={6}
          />
        </Section>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={() => handleExportJson()}>
          <Download className="h-4 w-4 mr-2" /> Export JSON
        </Button>
        <Button variant="outline" onClick={() => handleExportPdf()}>
          <FileText className="h-4 w-4 mr-2" /> Export PDF
        </Button>
        <Button onClick={handleSave} disabled={createQuestionnaire.isPending || updateQuestionnaire.isPending} className="bg-[hsl(60,73%,76%)] text-black border border-gray-300 hover:bg-[hsl(60,73%,70%)]">
          <Save className="h-4 w-4 mr-2" />
          {(createQuestionnaire.isPending || updateQuestionnaire.isPending) ? "Saving..." : editingId ? "Update Questionnaire" : "Save Questionnaire"}
        </Button>
      </div>
    </div>
  );
};
